import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as api from '../../api/app/monitoredProcessDataApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { AssetFullPathHeader } from '../../components/core/AssetFullPathHeader/AssetFullPathHeader';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { AssetDetailTabValues, MonitoredPanelValues } from '../../types/app/asset.types';
import {
  MonitoredProcessData,
  MonitoredProcessDataInputs,
} from '../../types/app/monitoredProcessData.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
import { MonitoredProcessDataForm } from './index';
import { useStyles } from './MonitoredProcessDataForm.styles';

export function MonitoredProcessDataDetail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();
  const [defaultValues, setDefaultValues] = useState<MonitoredProcessDataInputs | null>(null);

  const { mutate: mutateUpdateMonitoredProcessData } = useMutation(api.updateMonitoredProcessData);
  const { data, isLoading, isError } = useQuery<MonitoredProcessData>(
    ['monitoredProcessData', id],
    () => api.getMonitoredProcessData(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      parentAssetId: data.parentAssetId,
      opcClientId: data.opcClientId,
      influxValueName: data.influxValueName,
      nodeId: data.nodeId,
      name: data.name,
      main: data.main,
      dataType: data.dataType,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<MonitoredProcessDataInputs> = (
    data: MonitoredProcessDataInputs,
  ) => {
    if (!defaultValues) return;

    mutateUpdateMonitoredProcessData(
      { ...data },
      {
        onSuccess: () => {
          enqueueSnackbar(`MonitoredProcessData edited`, { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
            state: {
              tabValue: AssetDetailTabValues.MONITORED_VALUES,
              panelValue: MonitoredPanelValues.MONITORED_PROCESS_DATA,
            },
          });
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <AssetFullPathHeader
        finalTitle={dictionary.MONITORED_PROCESS_DATA_EDIT}
        parentId={Number(parentId)}
      />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid item xs={12} md={8} lg={4}>
              <Paper className={classes.paper}>
                <MonitoredProcessDataForm
                  onSubmit={handleOnSubmit}
                  onBack={() =>
                    navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                      state: {
                        tabValue: AssetDetailTabValues.MONITORED_VALUES,
                        panelValue: MonitoredPanelValues.MONITORED_PROCESS_DATA,
                      },
                    })
                  }
                  defaultValues={defaultValues}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
